import { LoginForm } from 'components/auth/LoginForm';
import { ScrollToTop } from 'components/common/ScrollToTop';
import { SideMenu } from 'components/ui/SideMenu';
import { useAppInitialization } from 'hooks/useAppInitialization';
import { BackButtonHeader } from 'layouts/BackButtonHeader';
import { CommonHeader } from 'layouts/HeaderLayout';
import { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, useLocation } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  arsCustomerInfoState,
  isLoginFormOpenState,
  isMenuOpenState,
  shopDetailState,
  shopState,
} from 'store/atoms';
import styled from 'styled-components';
import { useTranslation } from 'i18n';

const Container = styled.div`
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;

  /* 모바일 우선 반응형 디자인 */
  width: 100%;
  max-width: 430px; /* 모바일 기준 최대 너비 */
  min-height: 100dvh;
  margin: 0 auto;

  /* 큰 화면에서 조금 더 넓게 (600px) */
  @media (min-width: 1024px) {
    max-width: 600px;
  }
`;

const LoadingFallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: #666;
`;

// 서브페이지 경로와 제목 키 매핑 (기본값)
const SUBPAGE_CONFIG: Record<string, { titleKey: string; fallbackPath: string }> = {
  '/booking': { titleKey: 'page_title.book_tee_time', fallbackPath: '/' },
  '/membership': { titleKey: 'page_title.buy_membership', fallbackPath: '/' },
  '/complete': { titleKey: 'page_title.payment_detail', fallbackPath: '/' },
  '/my/membership/renewal': { titleKey: 'page_title.manage_membership', fallbackPath: '/my/membership' },
  '/event': { titleKey: 'page_title.event', fallbackPath: '/' },
};

// 서브페이지 설정 조회 (쿼리 파라미터 및 동적 경로 처리 포함)
function getSubpageConfig(
  pathname: string,
  search: string,
): { titleKey: string; fallbackPath: string } | null {
  const urlParams = new URLSearchParams(search);

  // 1. 쿼리 파라미터 기반 특수 케이스 처리 (최우선)
  if (pathname === '/payment') {
    const type = urlParams.get('type');

    if (type === 'membership') return { titleKey: 'page_title.become_member', fallbackPath: '/' };
    if (type === 'booking') return { titleKey: 'page_title.book_session', fallbackPath: '/' };
  }

  if (pathname.startsWith('/my/membership/') && urlParams.get('view') === 'payment') {
    return { titleKey: 'page_title.payment_detail', fallbackPath: '/' };
  }

  // 2. 정확한 경로 매칭
  if (SUBPAGE_CONFIG[pathname]) {
    return SUBPAGE_CONFIG[pathname];
  }

  // 3. 동적 경로 패턴 매칭 (우선순위: 더 구체적인 경로부터)
  if (pathname.startsWith('/my/membership/renewal/')) {
    return { titleKey: 'page_title.my_membership', fallbackPath: '/my/membership/renewal' };
  }
  if (pathname.startsWith('/my/booking/')) {
    return { titleKey: 'page_title.my_booking', fallbackPath: '/my/booking' };
  }
  if (pathname.startsWith('/my/membership/')) {
    return { titleKey: 'page_title.my_membership', fallbackPath: '/my/membership' };
  }
  if (pathname.startsWith('/event/')) {
    return { titleKey: 'page_title.event_detail', fallbackPath: '/event' };
  }

  return null;
}

export function RootLayout() {
  useAppInitialization();
  const { t } = useTranslation();

  const location = useLocation();
  const isMenuOpen = useRecoilValue(isMenuOpenState);
  const isLoginFormOpen = useRecoilValue(isLoginFormOpenState);
  const setIsMenuOpen = useSetRecoilState(isMenuOpenState);
  const setIsLoginFormOpen = useSetRecoilState(isLoginFormOpenState);
  const shop: any = useRecoilValue(shopState);
  const shopDetail = useRecoilValue(shopDetailState);
  const arsCustomerInfo = useRecoilValue(arsCustomerInfoState);
  const isKoreaShop = shopDetail?.solutionConfig?.global_config?.countryCode === 'KR';
  const hideHeader = isKoreaShop || !!arsCustomerInfo;

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleCloseLoginForm = () => {
    setIsLoginFormOpen(false);
  };

  const subpageConfig = getSubpageConfig(location.pathname, location.search);
  const isSubpage = subpageConfig !== null;

  // 커스텀 헤더 제목이 있으면 우선 사용 (각 페이지에서 설정 가능)
  const finalTitle = subpageConfig ? t(subpageConfig.titleKey) : '';

  return (
    <Container>
      <Helmet>
        <title>
          {shop?.shopName ? `${shop.shopName} | Kaddie` : t('meta.default_title')}
        </title>
        <meta
          name="description"
          content={
            shop?.shopName
              ? `Booking system for ${shop.shopName} | ${shop?.shopAddress || ''}`
              : t('meta.default_description')
          }
        />
      </Helmet>
      <ScrollToTop />
      {hideHeader ? null : isSubpage ? (
        <BackButtonHeader title={finalTitle} />
      ) : (
        <CommonHeader />
      )}
      <Suspense fallback={<LoadingFallback>{t('common.loading')}</LoadingFallback>}>
        <Outlet />
      </Suspense>
      <SideMenu isOpen={isMenuOpen} onClose={handleCloseMenu} />
      <LoginForm isOpen={isLoginFormOpen} onClose={handleCloseLoginForm} />
    </Container>
  );
}
